import React, { MouseEvent, useState } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import {
  black,
  heading3FontSize,
  cta1FontWeight,
  CollapseButton,
  cssColorValue,
  kleinBlue61,
  screenMdMax,
  screenSmMax,
  screenSmMin,
  screenXsMax,
  screenXxsMax,
  spacer16,
  spacer20,
  spacer24,
  spacer32,
  spacer40,
  spacer64,
  spacer8,
  Type,
  white,
} from '@spotify-internal/encore-web';
import { Link } from '../../ui';
import { FaqCategory, HELP_LIST_TRACK_CAT, hidden } from '../util';

import { useTracking } from '../../Tracking';
import { useT } from '@spotify-internal/i18n-core';

const borderRadius = '6px';

const Container = styled.div`
  display: flex;
  flex-flow: column nowrap;
  padding: 0;
  margin: 0;
`;

const TopicGridComponent = styled.div`
  justify-content: center;
  background: ${({ theme }) => theme.help.grayBox.background};
  color: ${({ theme }) => theme.help.grayBox.text};
  width: 100%;
  padding: ${spacer40} ${spacer8} ${spacer8} ${spacer40};

  @media (max-width: ${screenSmMax}) {
    padding: ${spacer40} ${spacer16} ${spacer16} ${spacer40};
  }

  @media (max-width: ${screenXsMax}) {
    padding: ${spacer32} 0 ${spacer16} ${spacer16};
    margin: 0;
  }
`;

const Headline = styled(Type.h2).attrs({
  paddingBottom: '0.75em',
  variant: 'alto',
  weight: 'bold',
})<{}>`
  padding: 0 0 ${spacer40} 0;

  @media (max-width: ${screenXsMax}) {
    padding: 0 0 ${spacer32} 0;
  }
`;

const Topics = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-content: flex-start;
  width: 100%;
`;

const Topic = styled(Link)`
  display: flex;
  flex-flow: column nowrap;
  border-radius: ${borderRadius};
  background: ${white};
  margin: 0 ${spacer32} ${spacer32} 0;
  padding: 0;
  overflow: hidden;
  width: calc(33.33% - ${spacer32});
  height: 208px;

  @media (max-width: ${screenMdMax}) {
    margin: 0 ${spacer24} ${spacer24} 0;
    width: calc(50% - ${spacer24});
  }

  @media (max-width: ${screenSmMax}) {
    height: 176px;
  }

  @media (max-width: ${screenXsMax}) {
    width: calc(100% - ${spacer16});
    margin: 0 0 ${spacer16} 0;
  }

  &:hover {
    cursor: pointer;
    color: unset;
  }

  &:hover h3 {
    color: ${cssColorValue('textSubdued')};
  }
`;

const TopicName = styled(Type.h3).attrs({
  variant: 'celloCanon',
})`
  flex: 0;
  padding: ${spacer20} ${spacer20} 0 ${spacer20};
  color: ${kleinBlue61};
  overflow: hidden;
  min-height: 58px;

  @media (max-width: ${screenXsMax}) {
    font-size: ${heading3FontSize};
    font-weight: ${cta1FontWeight};
  }
`;

const TopicContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-flow: row nowrap;
  height: 100%;

  @media (min-width: ${screenSmMin}) and (max-width: ${screenSmMax}),
    (min-width: 0) and (max-width: ${screenXxsMax}) {
    display: block;
    padding-right: ${spacer20};
  }
`;

const TopicTextContainer = styled.div`
  flex: 3;
  padding: 0 0 0 ${spacer20};
  max-height: 112px;
`;

const TopicIconContainer = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  max-width: 132px;

  @media (min-width: ${screenSmMin}) and (max-width: ${screenSmMax}),
    (min-width: 0) and (max-width: ${screenXxsMax}) {
    ${hidden}
  }
`;

const TopicCount = styled(Type.p).attrs({
  variant: 'viola',
})`
  color: ${cssColorValue('textSubdued')};
  padding-bottom: 8px;
`;

const TopicDescription = styled(Type.p).attrs({
  variant: 'ballad',
})``;

const TopicIcon = styled.img`
  transform: rotate(25deg) translate(20px);
`;

const CTA = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${spacer64};
  background-color: ${cssColorValue('decorativeSubdued')};

  &:hover {
    cursor: pointer;
  }
`;

const CTAText = styled(Type).attrs({
  variant: 'metronome',
})`
  padding-bottom: 0;
  text-transform: uppercase;
  color: ${black};
`;

export type TopicGridType = {
  headline?: string;
  items?: FaqCategory[];
  initialCount?: number;
};

export const TopicGrid: React.FC<TopicGridType> = ({
  headline,
  items = [],
  initialCount = 6,
}) => {
  const t = useT();
  const router = useRouter();
  const { eventHandler } = useTracking();
  const [displayAll, setDisplayAll] = useState(false);
  const seeLess = t('FRODOR_c59be2', 'See less', 'Show less information');
  const seeMore = t('FRODOR_0165e4', 'See more', 'Show more information');
  let ctaText = displayAll ? seeLess : seeMore;

  if (router && router.query && router.query._lqa === 'showSeeLess') {
    ctaText = seeLess;
  } else if (router && router.query && router.query._lqa === 'showSeeMore') {
    ctaText = seeMore;
  }

  const itemsToDisplay = displayAll ? items : items.slice(0, initialCount);

  const ctaOnClick = (e: MouseEvent<HTMLDivElement>) => {
    eventHandler(
      `Frodor - ${HELP_LIST_TRACK_CAT}`,
      `Topics CTA - ${ctaText}`,
    )(e);
    setDisplayAll(!displayAll);
  };

  return (
    (itemsToDisplay?.length && (
      <Container>
        <TopicGridComponent data-testid="help-topics">
          <Headline>
            {headline ||
              t(
                'FRODOR_TOPIC_GRID_HEADLINE',
                'Browse help topics',
                'Headline for help topics group',
              )}
          </Headline>
          <Topics>
            {itemsToDisplay.map((item, key) => (
              <Topic
                key={key}
                track={[HELP_LIST_TRACK_CAT, `Topics Link - ${item.name}`]}
                href={`/help/${item.urlSlug}`}
              >
                <TopicName>{item.name}</TopicName>
                <TopicContentContainer>
                  <TopicTextContainer>
                    <TopicCount>
                      {t(
                        'FRODOR_TOPICS_ARTICLES',
                        '{num} articles',
                        'This will be preceeded by a number',
                        { num: item.faqs?.length || 0 },
                      )}
                    </TopicCount>
                    <TopicDescription>
                      {item.description || ''}
                    </TopicDescription>
                  </TopicTextContainer>
                  <TopicIconContainer>
                    {item.icon && (
                      <TopicIcon src={`${item.icon?.url}`} aria-hidden />
                    )}
                  </TopicIconContainer>
                </TopicContentContainer>
              </Topic>
            ))}
          </Topics>
        </TopicGridComponent>
        {items.length > initialCount ||
        (router && router.query && router.query._lqa === 'showSeeLess') ||
        (router && router.query && router.query._lqa === 'showSeeMore') ? (
          <CTA onClick={ctaOnClick}>
            <CollapseButton expanded={displayAll} role="button">
              <CTAText>{ctaText}</CTAText>
            </CollapseButton>
          </CTA>
        ) : null}
      </Container>
    )) || <></>
  );
};
